/* eslint-disable */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, Box, Button, Card, CircularProgress, FormControl, FormHelperText, styled, TextField, useMediaQuery, useTheme } from "@mui/material";
import Typo from "typo-js";
import debounce from "lodash/debounce";
import locationJson from "../../utils/location.json";
import KeywordSuggestionPopup from "../Modal/components/KeywordSuggestionPopup";
import BackButton from "../BackButton/BackButton";
import { useNavigate } from "react-router";

const BoxWrapper = styled(Box)(({ theme }) => ({
  "& .FormWrapper": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "15px",
    "@media screen and (max-width:600px)": {
      flexDirection: "column",
    },
    "& .SearchFilterWrapper": {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: "15px",
      "@media screen and (max-width:600px)": {
        flexDirection: "column",
        width: "100%",
      },
      "& .MuiFormControl-root": {
        "& label": {
          marginBottom: "0",
          top: "50%",
          transform: "translate(14px, -50%)",
          "&.MuiInputLabel-shrink": {
            top: "0",
            transform: "translate(14px, -9px) scale(0.75)",
          },
        },
      },
      "& .LocationWrapper": {
        display: "flex",
        alignItems: "center",
        gap: "15px",
        width: "100%",
        "@media screen and (max-width:600px)": {
          flexDirection: "column",
        },
        "& .MuiAutocomplete-root": {
          "& .MuiFormControl-root": {
            "& .MuiInputBase-root": {
              padding: "5px",
            },
          },
        },
        "& .SearchIcon": {
          backgroundColor: theme.palette.primary.main,
          color: "#fff",
          minWidth: "auto",
          padding: "11px 10px 9px",
          width: "44px",
          height: "44px",
          "@media screen and (max-width:600px)": {
            width: "100%",
            height: "36px",
            padding: "10px 10px 10px",
          },
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
          "&.Mui-disabled": {
            opacity: "0.5",
          },
          "&:focus": {
            outline: "0",
          },
          "& .MuiCircularProgress-root": {
            color: "#fff",
          },
        },
      },
    },
  },
}));

function generateSentences(input) {
  function helper(arr, index, currentSentence, results) {
    if (index === arr.length) {
      results.push(currentSentence.join(" "));
      return;
    }

    for (let word of arr[index]) {
      helper(arr, index + 1, [...currentSentence, word], results);
    }
  }

  const results = [];
  helper(input, 0, [], results);
  return results;
}

function KeywordOverview({ queryObj, handleChangeDrop, HandleClickFunc, isLoading, errors }) {
  const theme = useTheme();
  const [typo, setTypo] = useState(null);
  const [keyWord, setKeyWord] = useState(queryObj.keywords || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isCorrect, setIsCorrect] = useState(true);
  const [open, setOpen] = useState(false);
  const isSmall = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/en_US.aff")
      .then(response => response.text())
      .then(affData => {
        fetch("/en_US.dic")
          .then(response => response.text())
          .then(dicData => {
            const typoInstance = new Typo("en_US", affData, dicData);
            setTypo(typoInstance);
          });
      });
  }, []);

  const spellCheck = useCallback(
    async text => {
      if (typo) {
        const words = text.trim().split(" ");
        let allCorrect = true;

        const test = await Promise.all(
          words.map(word => {
            if (!typo.check(word)) {
              const suggestions = typo.suggest(word);
              // correctedWords.push(
              allCorrect = false;

              return suggestions.length > 0 ? suggestions : [word];
            } else {
              // correctedWords.push([word]);
              return [word];
            }
          }),
        );
        const newSuggestions = generateSentences(test);
        setSuggestions(newSuggestions);
        setIsCorrect(allCorrect);
      }
    },
    [typo],
  );

  const debouncedSpellCheck = useMemo(() => debounce(spellCheck, 500), [spellCheck]);

  useEffect(() => {
    return () => {
      debouncedSpellCheck.cancel();
    };
  }, [debouncedSpellCheck]);

  useEffect(() => {
    if (keyWord.trim() !== "") {
      debouncedSpellCheck(keyWord);
    }
  }, [keyWord, debouncedSpellCheck]);

  const handleInputChange = useCallback(
    event => {
      const value = event.target.value;
      setKeyWord(value);

      // Notify parent about the change
      handleChangeDrop({
        target: { name: "keywords", value },
      });
    },
    [handleChangeDrop],
  );

  const handleSuggestionClick = useCallback(
    suggestedSentence => {
      setKeyWord(suggestedSentence);

      // Notify parent about the change
      handleChangeDrop({
        target: { name: "keywords", value: suggestedSentence },
      });
    },
    [handleChangeDrop],
  );
  const handleNavigateBack = () => {
    // navigate("/dashboard");
    navigate("/ai-blog-generator?menu=headerAiBlogGenerator");
  };

  return (
    <BoxWrapper>
      <Card
        sx={{
          padding: "16px 16px 16px 16px",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none !important",
        }}
      >
        <Box className="FormWrapper">
          <BackButton onClick={handleNavigateBack} sx={{ "& button": { marginBottom: "0 !important" } }} />
          <Box className="SearchFilterWrapper">
            <FormControl sx={{ minWidth: { xs: "100%", sm: "220px", position: "relative" } }}>
              <TextField
                id="outlined-basic"
                name="keywords"
                label="Enter your ads title"
                variant="outlined"
                value={keyWord}
                onChange={handleInputChange}
                error={!!errors.keywords}
              />
              {errors.keywords && <FormHelperText error>{errors.keywords}</FormHelperText>}
            </FormControl>
            <Box className="LocationWrapper">
              <FormControl sx={{ minWidth: { xs: "100%", sm: "300px" } }}>
                <Autocomplete
                  multiple={false}
                  value={locationJson.find(item => item.Geo_ID === queryObj.loc_id)}
                  onChange={(event, newValue) =>
                    handleChangeDrop({
                      target: { name: "loc_id", value: newValue },
                    })
                  }
                  options={locationJson}
                  getOptionLabel={option => option.Name || "select Geo location"}
                  renderInput={params => <TextField {...params} variant="outlined" label="Select Geo Location" error={!!errors.loc_id} />}
                />
                {errors.loc_id && <FormHelperText error>{errors.loc_id}</FormHelperText>}
              </FormControl>
              <Button
                className="SearchIcon"
                onClick={() => {
                  if (isCorrect) {
                    HandleClickFunc();
                  } else {
                    setOpen(true);
                  }
                }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : isSmall ? "Search" : <SearchIcon />}
              </Button>
              {!isCorrect && suggestions.length > 0 && (
                <KeywordSuggestionPopup
                  open={open}
                  onSubmit={HandleClickFunc}
                  onClose={() => {
                    setOpen(false);
                  }}
                  suggestions={suggestions}
                  handleSuggestionClick={handleSuggestionClick}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Card>
    </BoxWrapper>
  );
}

export default KeywordOverview;
